import dayjs from 'dayjs';
import { I18n } from '@aws-amplify/core';

import { StatusFile } from '@/utils/enum';
import { dateFormat } from '@/utils/date-format';
import type { BeraReportV2 } from '@/types/Bera';
import type { TaskResult } from '@/types/Bera';
import type {
	BeraClipFile,
	BeraClipStatusEvent,
	BeraResultAnalyzedFiles,
	BeraSummaryResultV2,
	BeraTaskResultV2,
	BeraVersion
} from '@/types/Bera';

export const RISK_TONE = {
	LOW: 'low',
	MEDIUM: 'medium',
	HIGH: 'high'
} as const;

export type RiskTone = (typeof RISK_TONE)[keyof typeof RISK_TONE];

const FIRST_VERSION_INDEX = 0;
const HIGH_SCORE_THRESHOLD = 45;
const MEDIUM_SCORE_THRESHOLD = 15;

export function toScoreTone(score: number): RiskTone {
	if (score > HIGH_SCORE_THRESHOLD) {
		return RISK_TONE.HIGH;
	}

	if (score > MEDIUM_SCORE_THRESHOLD) {
		return RISK_TONE.MEDIUM;
	}

	return RISK_TONE.LOW;
}

export function toEnsTone(isEns: boolean): RiskTone {
	if (isEns) {
		return RISK_TONE.LOW;
	}

	return RISK_TONE.HIGH;
}

export function findTaskReport(task: TaskResult, reports: BeraReportV2[]): BeraReportV2 | undefined {
	const result = reports.find((report) => report.id === task.bera_report_id);

	return result;
}

const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const SINGLE_UNIT = 1;
const MAX_FRACTION_DIGITS = 1;
const DECIMAL_COMMA = ',';
const DECIMAL_DOT = '.';

const TIME_UNIT = {
	SECOND: { singular: 'second', plural: 'seconds' },
	MINUTE: { singular: 'minute', plural: 'minutes' },
	HOUR: { singular: 'hour', plural: 'hours' }
} as const;

type TimeUnit = (typeof TIME_UNIT)[keyof typeof TIME_UNIT];

function toUnitLabel(value: number, unit: TimeUnit): string {
	if (value === SINGLE_UNIT) {
		return I18n.get(unit.singular);
	}

	return I18n.get(unit.plural);
}

function toTimeText(value: number, unit: TimeUnit): string {
	const formatted = value.toLocaleString(window.navigator.language, { maximumFractionDigits: MAX_FRACTION_DIGITS });
	const result = `${formatted} ${toUnitLabel(value, unit)}`;

	return result;
}

function toMinutes(totalMinutes: number | string): number {
	const normalized = String(totalMinutes).replace(DECIMAL_COMMA, DECIMAL_DOT);
	const result = Number(normalized);

	return result;
}

export function toAnalyzedTime(totalMinutes: number | string): string {
	const minutes = toMinutes(totalMinutes);

	if (minutes < SINGLE_UNIT) {
		const seconds = Math.round(minutes * SECONDS_PER_MINUTE);
		const result = toTimeText(seconds, TIME_UNIT.SECOND);

		return result;
	}

	if (minutes >= MINUTES_PER_HOUR) {
		const hours = minutes / MINUTES_PER_HOUR;
		const result = toTimeText(hours, TIME_UNIT.HOUR);

		return result;
	}

	const result = toTimeText(minutes, TIME_UNIT.MINUTE);

	return result;
}

export type AnalyzedActivity = BeraResultAnalyzedFiles & {
	status: StatusFile | null;
	video_url: string | null;
	processing_error: string | null;
};

export function isClipReady(activity: AnalyzedActivity): boolean {
	const result = activity.status === StatusFile.PROCESSED && !!activity.video_url;

	return result;
}

export function isClipCorrupted(activity: AnalyzedActivity): boolean {
	const result = activity.status === StatusFile.CORRUPTED_FILE;

	return result;
}

export function toAnalyzedActivities(
	analyzedFiles: BeraResultAnalyzedFiles[],
	tasks: BeraTaskResultV2[]
): AnalyzedActivity[] {
	const clipEntries = tasks.map((task) => [task.bera_report_id, task.file] as const);
	const clipsByReport = new Map(clipEntries);

	const result = analyzedFiles.map((analyzedFile) => {
		const clip = clipsByReport.get(analyzedFile.id);

		return {
			...analyzedFile,
			file_name: clip?.original_name ?? analyzedFile.file_name,
			status: clip?.status ?? null,
			video_url: clip?.url ?? null,
			processing_error: clip?.processing_error ?? null
		};
	});

	return result;
}

function toPatchedClipFile(task: BeraTaskResultV2, event: BeraClipStatusEvent): BeraClipFile {
	if (task.file) {
		return { ...task.file, status: event.file.status };
	}

	return {
		id: event.file.id,
		original_name: task.file_name,
		status: event.file.status,
		duration: null,
		url: null,
		processing_error: null
	};
}

export function patchClipStatus(
	summary: BeraSummaryResultV2 | undefined,
	event: BeraClipStatusEvent
): BeraSummaryResultV2 | undefined {
	if (!summary?.tasks) {
		return summary;
	}

	const tasks = summary.tasks.map((task) => {
		if (task.bera_report_id !== event.bera_report_id) {
			return task;
		}

		const file = toPatchedClipFile(task, event);

		return { ...task, file };
	});

	const result = { ...summary, tasks };

	return result;
}

export function getVersionLabel(version: BeraVersion, index: number): string {
	const date = dayjs(version.created_at).format(dateFormat());

	if (index === FIRST_VERSION_INDEX) {
		const initialLabel = `${I18n.get('Initial')} (${date})`;

		return initialLabel;
	}

	const revisionLabel = `${I18n.get('Revision')} ${index} (${date})`;

	return revisionLabel;
}
